import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { BidService } from "../../services";
import { asQuery } from "../../services/common";
import { usePersistStore } from "../../store";
import { AD_STALE, DASH_STALE } from "./shared";

// The signed-in user's bids (token-gated). Detail page uses this to tell
// whether the user already bid on the artwork being viewed.
export function useMyBids(token?: string) {
  return useQuery({
    queryKey: ["bids", "mine", token],
    queryFn: () => asQuery<any>(BidService.getMine(token as string, {})),
    enabled: !!token,
    staleTime: AD_STALE,
  });
}

// Paginated "My Bids" dashboard table ({ bids, count }).
export function useMyBidsList(token: string | undefined, params: any) {
  return useQuery({
    queryKey: ["bids", "mine-list", token, params],
    queryFn: () => asQuery<any>(BidService.getMine(token as string, params)),
    enabled: !!token,
    staleTime: DASH_STALE,
    placeholderData: keepPreviousData,
  });
}

// Bid mutations leave toasts/loading/validation to the modal (its success
// modal + wording are bespoke) and only own the service call + invalidation:
// the user's bids and the affected ad's detail (its bid count) both refresh.
export function useCreateBid() {
  const qc = useQueryClient();
  const { token } = usePersistStore((s: any) => s);
  return useMutation({
    mutationFn: (vars: { adId: string; price: string }) =>
      asQuery<any>(
        BidService.create(token, { price: vars.price, adId: vars.adId }),
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["bids", "mine"] });
      qc.invalidateQueries({ queryKey: ["ads", "details", vars.adId] });
    },
  });
}

export function useUpdateBid() {
  const qc = useQueryClient();
  const { token } = usePersistStore((s: any) => s);
  return useMutation({
    mutationFn: (vars: { bidId: string; price: string; adId?: string }) =>
      asQuery<any>(BidService.update(vars.bidId, token, { price: vars.price })),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["bids", "mine"] });
      if (vars.adId)
        qc.invalidateQueries({ queryKey: ["ads", "details", vars.adId] });
    },
  });
}
